<!-- src/views/socios/SocioDetallePage.vue -->
<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchSocio } from './services/socios.api'
import MembresiasTab from './components/MembresiasTab.vue'
import PagosTab from './components/PagosTab.vue'
import Swal from 'sweetalert2'

import { encodeId, decodeId } from '@/utils/obfuscate'

const route = useRoute()
const router = useRouter()
const usuarioId = Number(decodeId(route.params.id))

const socio = ref(null)
const loading = ref(true)

const activeTab = ref('membresias') // 'membresias' | 'pagos'

const avatarError = ref(false)
function onAvatarError() {
    avatarError.value = true
}

const iniciales = computed(() => {
    const n = socio.value?.nombre?.trim() || ''
    if (!n) return '—'
    const parts = n.split(/\s+/).slice(0, 2) // máx 2 palabras
    return parts.map(p => p[0]?.toUpperCase() || '').join('') || '—'
})

const tituloRol = computed(() => {
    const nombre = socio.value?.rol?.nombre || socio.value?.rol || ''
    const norm = String(nombre).trim().toUpperCase()
    if (['ADMINISTRADOR','EMPLEADO','SOCIO'].includes(norm)) return norm
    // Fallback por compatibilidad si backend solo devuelve rol_id sin include
    const id = socio.value?.rol_id
    // No hardcodear decisión funcional; solo etiqueta visual tolerante
    if (id != null) return String(nombre || '—').toUpperCase() || '—'
    return '—'
})

async function cargarSocio() {
    try {
        loading.value = true
        socio.value = await fetchSocio(usuarioId)
    } catch (e) {
        const msg = e.response?.data?.message || e.message
        Swal.fire('Error', msg || 'No se pudo cargar el socio', 'error')
        router.push({ name: 'Socios' })
    } finally {
        loading.value = false
    }
}

function goEdit() {
    router.push({ name: 'UsuarioEditar', params: { id: encodeId(usuarioId) } })
}

onMounted(cargarSocio)
</script>

<template>
    <div class="container-fluid py-4">
        <div class="card p-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">Detalle de socio</h5>
                <div>
                    <button class="btn btn-outline-secondary btn-sm me-2" @click="$router.back()">Volver</button>
                    <button class="btn btn-primary btn-sm"
                        @click="goEdit">
                        Editar socio
                    </button>
                </div>
            </div>

            <div v-if="loading" class="text-muted">Cargando...</div>

            <template v-else>
                <!-- Header del socio -->
                <div class="row g-3 mb-3">

                    <!-- Columna izquierda: avatar + datos principales -->
                    <div class="col-12 col-md-6">
                        <div class="p-3 bg-light rounded-3 h-100 d-flex align-items-center gap-3">

                            <!-- Avatar con fallback a iniciales -->
                            <div class="avatar-wrapper">
                                <img v-if="socio?.avatar_url && !avatarError" :src="socio.avatar_url" alt="Avatar"
                                    class="avatar-img" loading="lazy" decoding="async" @error="onAvatarError" />
                                <div v-else class="avatar-fallback">{{ iniciales }}</div>
                            </div>

                            <!-- Datos -->
                            <div>
                                <div class="fw-semibold">{{ socio?.nombre }}</div>
                                <div class="small text-muted">ID: {{ socio?.id }} — Rol: {{ tituloRol }}</div>
                                <div class="small text-muted">Identificación: {{ socio?.identificacion }}</div>
                                <div class="small text-muted">Email: {{ socio?.email || '—' }}</div>
                                <div class="small text-muted">Teléfono: {{ socio?.telefono || '—' }}</div>
                            </div>

                        </div>
                    </div>

                    <!-- Columna derecha: otros datos -->
                    <div class="col-12 col-md-6">
                        <div class="p-3 bg-light rounded-3 h-100">
                            <div class="small">Dirección: {{ socio?.direccion || '—' }}</div>
                            <div class="small">Nacimiento: {{ socio?.fecha_nacimiento || '—' }}</div>
                            <div class="small">
                                Estado:
                                <span :class="['badge', socio?.estado ? 'bg-success' : 'bg-secondary']">
                                    {{ socio?.estado ? 'ACTIVO' : 'INACTIVO' }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>


                <!-- Tabs -->
                <ul class="nav nav-tabs">
                    <li class="nav-item">
                        <button class="nav-link" :class="{ active: activeTab === 'membresias' }"
                            @click="activeTab = 'membresias'">
                            Membresías
                        </button>
                    </li>
                    <li class="nav-item">
                        <button class="nav-link" :class="{ active: activeTab === 'pagos' }"
                            @click="activeTab = 'pagos'">
                            Pagos
                        </button>
                    </li>
                </ul>

                <div class="tab-content p-3 border border-top-0 rounded-bottom">
                    <div v-if="activeTab === 'membresias'">
                        <MembresiasTab :usuario-id="usuarioId" :socio="socio" />
                    </div>
                    <div v-if="activeTab === 'pagos'">
                        <PagosTab :usuario-id="usuarioId" />
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped>
.nav-link {
    cursor: pointer;
}

.avatar-wrapper {
    width: 72px;
    height: 72px;
    border-radius: 12px;
    overflow: hidden;
    flex: 0 0 auto;
    background: #f0f2f5;
    display: grid;
    place-items: center;
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.08);
}

.avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.avatar-fallback {
    width: 100%;
    height: 100%;
    display: grid;
    place-items: center;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: #445;
    background: linear-gradient(180deg, #eef1f6, #e7ebf2);
    user-select: none;
}
</style>
